import { observer } from "mobx-react";
import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  IconButton,
  useTheme,
  Grid2,
} from "@mui/material";
import CustomTypography from "../../../components/common/form/CustomTypography";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Input as InputIcon,
} from "@mui/icons-material";
import AppSearchDropDown2 from "../../../components/common/filter/AppSearchDropdown2";
import { FilterValue } from "../../../../models/Filters/FilterValue";
import CustomTextField from "../../../components/common/textFields/TextField";
import DatePicker2 from "../../../components/common/textFields/DatePicker2";
import dayjs from "dayjs";
import MediaAssetsUpload from "../../../components/common/upload/MediaAssetsUpload";
import { useStores } from "../../../../use-stores";
import { SwageazyInventoryProductRequestVariant } from "../../../../models/SwageazyInventory/SwageazyInventory";
import ImageAPI from "../../../../api/ImageAPI";
import { useNotification } from "../../../../utils/NotificationsContext";
import AppButton from "../../../components/common/buttons/AppButton";
import {
  PurchaseOrders,
  PurchaseOrderProduct,
} from "../../../../models/SwageazyInventory/SwageazyInventory";
import { ProductVariant } from "../../../../models/Products/ProductVariant";
import { SwageazyInventoryInwardRequestFormData } from "../../../../models/SwageazyInventory/SwageazyInventory";
import { Warehouse } from "../../../../models/Warehouse/Warehouse";
interface SwageazyInventoryInwardPopupProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const SwageazyInventoryInwardPopup: React.FC<
  SwageazyInventoryInwardPopupProps
> = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const { swageazyInventoryStore, warehouseStore, purchaseRequestStore } =
    useStores();
  const { showError } = useNotification();

  useEffect(() => {
    if (!open) {
      swageazyInventoryStore.resetInwardRequestFormData();
      if (swageazyInventoryStore.apiTimer) {
        clearTimeout(swageazyInventoryStore.apiTimer);
        swageazyInventoryStore.clearApiTimer();
      }
    } else {
      handleWarehouseSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handlePoSearch = (searchText?: string) => {
    swageazyInventoryStore.clearApiTimer();
    if (searchText && searchText.length > 0) {
      const timer = setTimeout(() => {
        swageazyInventoryStore
          .getPurchaseOrderNumbers(searchText)
          .catch((err) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            );
          });
      }, 1000);
      swageazyInventoryStore.setApiTimer(timer);
    } else {
      swageazyInventoryStore.setPoSearchResults([]);
      swageazyInventoryStore.setPoProducts([]);
      swageazyInventoryStore.setInwardRequestFormData({
        po_id: undefined,
        product_id: undefined,
        variants: [],
      });
    }
  };

  const handlePoProductSelect = (selected: FilterValue[]) => {
    if (selected?.length > 0) {
      const poId = Number(selected[0]?.id);
      if (!isNaN(poId) && poId > 0) {
        try {
          swageazyInventoryStore.getPurchaseRequestDetailByPOId(poId);
        } catch (error) {
          console.log("error", error);
        }

        swageazyInventoryStore.setInwardRequestFormData({
          po_id: poId,
        });
        getPurchaseOrderProducts(poId);
      }
    } else {
      swageazyInventoryStore.setInwardRequestFormData({
        po_id: undefined,
        product_id: undefined,
      });
      swageazyInventoryStore.setPoProducts([]);
      swageazyInventoryStore.clearPurchaseRequestDetail();
      swageazyInventoryStore.setInwardRequestFormData({
        variants: [],
      });
    }
  };

  const handleProductSelect = (selected: FilterValue[]) => {
    if (selected?.length > 0) {
      const selectedProductId = Number(selected[0]?.id);

      const selectedProduct = swageazyInventoryStore.poProducts?.find(
        (product) => product.product_id === selectedProductId,
      );

      if (selectedProduct) {
        swageazyInventoryStore.setInwardRequestFormData({
          product_id: selectedProduct.product_id,
        });

        if (
          selectedProduct.variants &&
          Array.isArray(selectedProduct.variants) &&
          selectedProduct.variants.length > 0
        ) {
          const initialVariant: SwageazyInventoryProductRequestVariant = {
            sku: `variant-${Date.now()}`,
            quantity_rejected: undefined,
            rejection_reason: undefined,
            variant_options: {},
          };
          swageazyInventoryStore.setInwardRequestFormData({
            variants: [initialVariant],
          });
        } else {
          swageazyInventoryStore.setInwardRequestFormData({
            variants: [],
          });
        }
      }
    } else {
      swageazyInventoryStore.setInwardRequestFormData({
        product_id: undefined,
        variants: [],
      });
    }
  };

  const addVariant = () => {
    const newVariant: SwageazyInventoryProductRequestVariant = {
      sku: `variant-${Date.now()}`,
      quantity: undefined,
      quantity_rejected: undefined,
      rejection_reason: undefined,
      expiry_date: undefined,
      variant_options: {},
    };
    swageazyInventoryStore.setInwardRequestFormData({
      variants: [
        ...(swageazyInventoryStore.inwardRequestFormData?.variants || []),
        newVariant,
      ],
    });
  };

  const removeVariant = (id: string) => {
    swageazyInventoryStore.setInwardRequestFormData({
      variants:
        (swageazyInventoryStore.inwardRequestFormData?.variants || []).filter(
          (v) => (v.sku || "") !== id,
        ) || [],
    });
  };

  const updateVariant = (
    id: string,
    field: keyof SwageazyInventoryProductRequestVariant,
    value: string | number | undefined,
  ) => {
    swageazyInventoryStore.setInwardRequestFormData({
      variants:
        swageazyInventoryStore.inwardRequestFormData.variants?.map((v) =>
          (v.sku || "") === id ? { ...v, [field]: value } : v,
        ) || [],
    });
  };

  const updateVariantOption = (
    id: string,
    optionName: string,
    value: string | undefined,
  ) => {
    swageazyInventoryStore.setInwardRequestFormData({
      variants:
        swageazyInventoryStore.inwardRequestFormData?.variants?.map((v) => {
          if ((v.sku || "") === id) {
            const updatedVariant = { ...v };
            if (!updatedVariant.variant_options) {
              updatedVariant.variant_options = {};
            }
            if (value) {
              updatedVariant.variant_options[optionName] = value;
            } else {
              delete updatedVariant.variant_options[optionName];
            }
            return updatedVariant;
          }
          return v;
        }) || [],
    });
  };

  // const handleCitySearch = (searchText?: string) => {
  //   swageazyInventoryStore.clearApiTimer();
  //   if (searchText && searchText.length > 2) {
  //     const timer = setTimeout(() => {
  //       addressStore
  //         .getCity({
  //           page_no: 1,
  //           search_text: searchText,
  //         })
  //         .catch((err) => {});
  //     }, 1000);
  //     swageazyInventoryStore.setApiTimer(timer);
  //   }
  // };

  const handleWarehouseSearch = () => {
    if (warehouseStore.warehouses.length === 0) {
      warehouseStore
        .searchWarehouse()
        .then(() => {
          if (warehouseStore.warehouses.length === 1) {
            swageazyInventoryStore.setInwardRequestFormData({
              warehouse_id: warehouseStore.warehouses[0].id,
              warehouse_name: warehouseStore.warehouses[0].identifier,
            });
          }
        })
        .catch(() => {});
    }
  };

  const handleWarehouseRackSearch = (
    warehouseId: string | number | undefined,
  ) => {
    if (warehouseId) {
      warehouseStore
        .searchWarehouseRack(String(warehouseId), "")
        .then(async () => {
          if (warehouseStore.warehouseRacks?.length === 1) {
            const autoSelectedRackId =
              warehouseStore.warehouseRacks[0]?.id || "";
            swageazyInventoryStore.setInwardRequestFormData({
              current_rack: autoSelectedRackId,
              box_id: undefined,
            });
          } else {
            swageazyInventoryStore.setInwardRequestFormData({
              current_rack: undefined,
              box_id: undefined,
            });
          }
        })
        .catch((err) => {
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          );
          swageazyInventoryStore.setInwardRequestFormData({
            current_rack: undefined,
            box_id: undefined,
          });
        });
    } else {
      warehouseStore.clearWarehouseRacks();
      swageazyInventoryStore.setInwardRequestFormData({
        current_rack: undefined,
        box_id: undefined,
      });
    }
  };

  const getPurchaseOrderProducts = async (poId?: number) => {
    const purchaseOrderId =
      poId ?? swageazyInventoryStore.inwardRequestFormData?.po_id ?? 0;
    if (purchaseOrderId <= 0) return;

    const response =
      await swageazyInventoryStore.swageazyInventoryAPI.getPurchaseOrderNumbers(
        String(purchaseOrderId),
      );
    if (response) {
      let products: PurchaseOrderProduct[] = [];
      let purchaseOrders: PurchaseOrders[] = [];

      if (response.purchase_orders && Array.isArray(response.purchase_orders)) {
        purchaseOrders = response.purchase_orders;
      } else if (Array.isArray(response)) {
        purchaseOrders = response;
      } else if (
        response.purchase_orders &&
        Array.isArray(response.purchase_orders)
      ) {
        purchaseOrders = response.purchase_orders;
      } else if (
        response.purchase_orders &&
        Array.isArray(response.purchase_orders)
      ) {
        purchaseOrders = response.purchase_orders;
      }

      if (purchaseOrders.length > 0) {
        const selectedPO = purchaseOrders.find(
          (po: PurchaseOrders) =>
            po.po_id === purchaseOrderId ||
            String(po.po_id) === String(purchaseOrderId),
        );
        if (
          selectedPO &&
          selectedPO.products &&
          Array.isArray(selectedPO.products)
        ) {
          products = selectedPO.products;
        }
      }

      const mergedProductsMap = new Map<number, PurchaseOrderProduct>();

      products.forEach((product) => {
        const productId = product.product_id;

        if (mergedProductsMap.has(productId)) {
          const existingProduct = mergedProductsMap.get(productId)!;
          existingProduct.quantity =
            (existingProduct.quantity || 0) + (product.quantity || 0);
        } else {
          mergedProductsMap.set(productId, {
            ...product,
            quantity: product.quantity || 0,
            available_pr_quantity: product.available_pr_quantity || 0,
          });
        }
      });

      const mergedProducts = Array.from(mergedProductsMap.values());

      swageazyInventoryStore.setPoProducts(mergedProducts);
    }
  };

  const canSubmit = () => {
    const formData = swageazyInventoryStore.inwardRequestFormData;

    if (!formData?.warehouse_name) return false;
    if (!formData?.current_rack) return false;
    if (!formData?.box_id || formData.box_id.trim() === "") return false;
    if (!formData?.po_id) return false;
    if (!formData?.product_id) return false;
    if (!swageazyInventoryStore.purchaseRequestDetail?.vendor_id) return false;

    const selectedProduct = swageazyInventoryStore.poProducts?.find(
      (p) => p?.product_id === formData.product_id,
    );

    if (!selectedProduct) return false;

    const hasVariants =
      selectedProduct?.variants &&
      Array.isArray(selectedProduct.variants) &&
      selectedProduct.variants.length > 0;

    if (!hasVariants) return false;

    const uniqueOptionNames: string[] = [];
    if (hasVariants) {
      selectedProduct?.variants?.forEach((v: ProductVariant) => {
        if (v?.options_name && !uniqueOptionNames.includes(v.options_name)) {
          uniqueOptionNames.push(v.options_name);
        }
      });
    }

    const variants = formData?.variants || [];
    if (variants.length === 0) return false;
    const isProductNonEatable =
      (selectedProduct as { expiry?: boolean } | undefined)?.expiry === false;

    if (
      isProductNonEatable &&
      variants.some((variant) => !!variant?.expiry_date)
    ) {
      return false;
    }

    for (const variant of variants) {
      if (!variant?.variant_options) return false;

      for (const optionName of uniqueOptionNames) {
        const optionValue = variant.variant_options[optionName];
        if (!optionValue || optionValue.trim() === "") {
          return false;
        }
      }

      if (
        variant?.quantity === undefined ||
        variant.quantity === null ||
        variant.quantity <= 0
      ) {
        return false;
      }

      if (
        variant?.quantity_rejected !== undefined &&
        variant.quantity_rejected !== null &&
        variant.quantity_rejected > 0
      ) {
        if (
          !variant?.rejection_reason ||
          variant.rejection_reason.trim() === ""
        ) {
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = () => {
    swageazyInventoryStore.setIsLoading(true);
    if (canSubmit()) {
      if (
        swageazyInventoryStore.inwardRequestFormData?.images &&
        swageazyInventoryStore.inwardRequestFormData?.images?.length > 0
      ) {
        const imageAPI = new ImageAPI();
        imageAPI
          .uploadImage(
            swageazyInventoryStore.inwardRequestFormData?.images,
            "product-images",
          )
          .then((uploadedImageUrls) => {
            swageazyInventoryStore.updateInwardRequestFormData(
              "qc_images",
              uploadedImageUrls,
            );
            onSave();
          })
          .catch((error) => {
            showError(
              "Error Occurred",
              error.response?.data?.message || error.message,
            );
          })
          .finally(() => {
            swageazyInventoryStore.setIsLoading(false);
          });
      } else {
        onSave();
        swageazyInventoryStore.setIsLoading(false);
      }
    } else {
      swageazyInventoryStore.setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            minHeight: { xs: "30vh", sm: "28vh" },
            maxHeight: { xs: "90vh", sm: "85vh", md: "75vh", lg: "70vh" },
            maxWidth: {
              xs: "80vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            width: {
              xs: "80vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          height: "100%",
          minHeight: { xs: "35vh", sm: "32vh" },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box>
            {/* <CustomTypography
              variant="body2"
              style={{
                color: theme.palette.customColors.greyTextColor,
                marginBottom: 4,
              }}
            >
              Order: Swageazy Inventory
            </CustomTypography> */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <InputIcon
                sx={{
                  color: theme.palette.customColors.blueGradientStart,
                  fontSize: 24,
                  marginTop: 0.2,
                }}
              />
              <CustomTypography variant="heading3">
                Swageazy Inventory Inward
              </CustomTypography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Grid2 container spacing={2} alignItems="stretch">
              <Grid2
                size={{ xs: 12, sm: 4, md: 4 }}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  PO Number{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <AppSearchDropDown2
                  title="PO Number"
                  isMultiSelect={false}
                  list={swageazyInventoryStore.poSearchResults || []}
                  value={
                    swageazyInventoryStore.inwardRequestFormData?.po_id
                      ? [
                          {
                            id: String(
                              swageazyInventoryStore.inwardRequestFormData
                                ?.po_id,
                            ),
                            value: String(
                              swageazyInventoryStore.inwardRequestFormData
                                ?.po_id,
                            ),
                          },
                        ]
                      : []
                  }
                  handleSelect={handlePoProductSelect}
                  handleSearch={handlePoSearch}
                  local={false}
                  minWidth="100%"
                  borderRadius="8px"
                />
              </Grid2>

              <Grid2
                size={{ xs: 12, sm: 4, md: 4 }}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  PO products{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <AppSearchDropDown2
                  title="PO Products"
                  isMultiSelect={false}
                  list={
                    swageazyInventoryStore.poProducts?.map((product) => ({
                      id: String(product?.product_id),
                      value: `${product?.name}  (${product?.quantity})`,
                    })) || []
                  }
                  value={
                    swageazyInventoryStore.inwardRequestFormData.product_id
                      ? [
                          {
                            id: String(
                              swageazyInventoryStore.inwardRequestFormData
                                .product_id,
                            ),
                            value: (() => {
                              const product =
                                swageazyInventoryStore.poProducts?.find(
                                  (p) =>
                                    p?.product_id ===
                                    swageazyInventoryStore.inwardRequestFormData
                                      .product_id,
                                );
                              return product
                                ? `${product?.name}  (${product?.quantity})`
                                : "";
                            })(),
                          },
                        ]
                      : []
                  }
                  handleSelect={handleProductSelect}
                  local={true}
                  minWidth="100%"
                  borderRadius="8px"
                />
                {swageazyInventoryStore.inwardRequestFormData?.product_id && (
                  <CustomTypography
                    variant="body2"
                    style={{
                      color: theme.palette.text.secondary,
                      marginTop: 8,
                      fontSize: "0.75rem",
                    }}
                  >
                    {(() => {
                      const selectedProduct =
                        swageazyInventoryStore.poProducts?.find(
                          (p) =>
                            p?.product_id ===
                            swageazyInventoryStore.inwardRequestFormData
                              ?.product_id,
                        );
                      if (selectedProduct?.quantity !== undefined) {
                        return `PR Qty: ${
                          selectedProduct.quantity
                        } / Available PR Qty: ${
                          selectedProduct.available_pr_quantity || 0
                        }`;
                      }
                      return null;
                    })()}
                  </CustomTypography>
                )}
              </Grid2>

              <Grid2
                size={{ xs: 12, sm: 4, md: 4 }}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  Vendor Details
                </CustomTypography>
                <Box
                  sx={{
                    minHeight: 40,
                    border: `1px solid ${theme.palette.customColors.greyBorder}`,
                    borderRadius: "8px",
                    px: 1.5,
                    py: 1.25,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    backgroundColor:
                      theme.palette.customColors?.lightGreyColor || "#F8FAFC",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: theme.palette.text.primary, minWidth: 88 }}
                    >
                      Vendor ID:
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {swageazyInventoryStore.purchaseRequestDetail
                        ?.vendor_id ?? "-"}
                    </CustomTypography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: theme.palette.text.primary, minWidth: 88 }}
                    >
                      Vendor Name:
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {swageazyInventoryStore.purchaseRequestDetail
                        ?.vendor_name || "-"}
                    </CustomTypography>
                  </Box>
                </Box>
              </Grid2>
            </Grid2>

            {swageazyInventoryStore.inwardRequestFormData?.product_id && (
              <Box>
                {(() => {
                  const selectedProduct =
                    swageazyInventoryStore.poProducts?.find(
                      (p) =>
                        p?.product_id ===
                        swageazyInventoryStore.inwardRequestFormData
                          ?.product_id,
                    );

                  const hasVariants =
                    selectedProduct?.variants &&
                    Array.isArray(selectedProduct.variants) &&
                    selectedProduct.variants.length > 0;

                  const uniqueOptionNames: string[] = [];
                  const variantsByOptionName: Record<string, ProductVariant[]> =
                    {};

                  if (hasVariants) {
                    selectedProduct?.variants?.forEach((v: ProductVariant) => {
                      if (
                        v?.options_name &&
                        !uniqueOptionNames.includes(v.options_name)
                      ) {
                        uniqueOptionNames.push(v.options_name);
                        variantsByOptionName[v.options_name] = [];
                      }
                      if (v?.options_name) {
                        variantsByOptionName[v.options_name]?.push(v);
                      }
                    });
                  }

                  const optionLists: Record<
                    string,
                    { id: string; value: string }[]
                  > = {};
                  uniqueOptionNames.forEach((optionName) => {
                    optionLists[optionName] =
                      variantsByOptionName[optionName]?.map(
                        (v: ProductVariant) => ({
                          id: String(v?.id),
                          value: v?.title,
                        }),
                      ) || [];
                  });

                  if (!hasVariants) {
                    return (
                      <Box
                        sx={{
                          padding: 2,
                          textAlign: "center",
                          color: theme.palette.customColors.greyTextColor,
                        }}
                      >
                        <CustomTypography variant="body2">
                          No variant found
                        </CustomTypography>
                      </Box>
                    );
                  }

                  return (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          justifyContent: "space-between",
                          alignItems: { xs: "stretch", sm: "center" },
                          gap: { xs: 1.5, sm: 0 },
                          marginBottom: 2,
                        }}
                      >
                        <CustomTypography
                          variant="heading6"
                          style={{
                            color: theme.palette.customColors.blackBorder,
                            fontWeight: 600,
                          }}
                        >
                          Variants{" "}
                          <span
                            style={{
                              color: theme.palette.error.main,
                              marginLeft: 4,
                            }}
                          >
                            *
                          </span>
                        </CustomTypography>
                        <AppButton
                          onClick={addVariant}
                          variant="primary"
                          label="Add Variant"
                          customStyles={{
                            height: 36,
                            minHeight: 36,
                            alignSelf: { xs: "stretch", sm: "auto" },
                          }}
                        />
                      </Box>

                      {swageazyInventoryStore.inwardRequestFormData?.variants?.map(
                        (
                          variant: SwageazyInventoryProductRequestVariant,
                          index: number,
                        ) => (
                          <Box
                            key={variant?.sku || `variant-${index}`}
                            sx={{
                              marginBottom: 2,
                              padding: { xs: 1.5, sm: 2 },
                              border: `1px solid ${theme.palette.customColors.greyBorder}`,
                              borderRadius: "8px",
                              backgroundColor:
                                theme.palette.customColors.lightGreyColor,
                            }}
                          >
                            <Grid2
                              container
                              spacing={2}
                              sx={{
                                width: "100%",
                                alignItems: "flex-start",
                              }}
                            >
                              {uniqueOptionNames.map((optionName) => (
                                <Grid2
                                  key={optionName}
                                  size={{ xs: 12, sm: 6, md: 3 }}
                                  sx={{ minWidth: 0 }}
                                >
                                  <AppSearchDropDown2
                                    title={`Select ${optionName}`}
                                    list={optionLists[optionName] || []}
                                    value={
                                      variant?.variant_options?.[optionName]
                                        ? [
                                            {
                                              id: variant?.variant_options?.[
                                                optionName
                                              ],
                                              value:
                                                variant?.variant_options?.[
                                                  optionName
                                                ],
                                            },
                                          ]
                                        : []
                                    }
                                    handleSelect={(selected) =>
                                      updateVariantOption(
                                        variant?.sku || `variant-${index}`,
                                        optionName,
                                        selected?.length > 0
                                          ? selected?.[0]?.value
                                          : undefined,
                                      )
                                    }
                                    local={true}
                                    minWidth="100%"
                                    borderRadius="8px"
                                  />
                                </Grid2>
                              ))}
                              <Grid2
                                size={{ xs: 12, sm: 6, md: 3 }}
                                sx={{ minWidth: 0 }}
                              >
                                <CustomTextField
                                  width="100%"
                                  label="Qty Received"
                                  placeholder="Qty Received"
                                  value={variant?.quantity}
                                  onChange={(e) =>
                                    updateVariant(
                                      String(variant?.sku),
                                      "quantity",
                                      Number(e.target.value),
                                    )
                                  }
                                  type="number"
                                  allowNegativeValue={false}
                                  allowDecimalValue={false}
                                />
                              </Grid2>
                              <Grid2
                                size={{ xs: 12, sm: 6, md: 3 }}
                                sx={{ minWidth: 0 }}
                              >
                                <CustomTextField
                                  width="100%"
                                  label="Qty Rejected"
                                  placeholder="Qty Rejected"
                                  value={variant?.quantity_rejected}
                                  onChange={(e) =>
                                    updateVariant(
                                      String(variant?.sku),
                                      "quantity_rejected",
                                      Number(e.target.value),
                                    )
                                  }
                                  allowNegativeValue={false}
                                  allowDecimalValue={false}
                                  type="number"
                                />
                              </Grid2>
                              <Grid2
                                size={{ xs: 12, sm: 6, md: 3 }}
                                sx={{ minWidth: 0 }}
                              >
                                {(() => {
                                  return (
                                    <DatePicker2
                                      type="date"
                                      label={
                                        Boolean(selectedProduct.has_expiry)
                                          ? "Expiry date (required)"
                                          : "Expiry date (optional)"
                                      }
                                      value={
                                        variant?.expiry_date
                                          ? dayjs(variant.expiry_date)
                                          : null
                                      }
                                      onChange={(newValue) =>
                                        updateVariant(
                                          String(variant?.sku),
                                          "expiry_date",
                                          newValue
                                            ? newValue.format("YYYY-MM-DD")
                                            : undefined,
                                        )
                                      }
                                      // disabled={
                                      //   !Boolean(selectedProduct.has_expiry)
                                      // }
                                      error={Boolean(
                                        selectedProduct.has_expiry,
                                      )}
                                      require={Boolean(
                                        selectedProduct.has_expiry,
                                      )}
                                      borderRadius="8px"
                                      borderColor="#E0E0E0"
                                      backgroundColor={
                                        theme.palette.customColors
                                          .grayBackGroundColor
                                      }
                                      zIndex={4000}
                                    />
                                  );
                                })()}
                              </Grid2>
                              <Grid2
                                size={{ xs: 12, sm: 12, md: 6 }}
                                sx={{ minWidth: 0 }}
                              >
                                <CustomTextField
                                  width="100%"
                                  label="Rejection Reason"
                                  placeholder="Rejection Reason"
                                  value={variant?.rejection_reason}
                                  type="text"
                                  onChange={(e) =>
                                    updateVariant(
                                      String(variant?.sku),
                                      "rejection_reason",
                                      e.target.value,
                                    )
                                  }
                                />
                              </Grid2>
                              <Grid2
                                size={{ xs: 12, sm: 12, md: 6 }}
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <IconButton
                                  onClick={() =>
                                    removeVariant(
                                      variant?.sku || `variant-${index}`,
                                    )
                                  }
                                  aria-label="Remove variant"
                                  sx={{
                                    color: theme.palette.customColors.pinkDark,
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.customColors.pinkLight,
                                    },
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid2>
                            </Grid2>
                          </Box>
                        ),
                      )}
                    </>
                  );
                })()}
              </Box>
            )}
          </Box>

          <Divider sx={{ marginBottom: 1 }} />

          <Box
            sx={{
              mb: 1,
              p: 2,
              backgroundColor: theme.palette.customColors.lightGreyColor,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.customColors.greyBorder}`,
            }}
          >
            <CustomTypography
              variant="body2"
              style={{
                color: theme.palette.text.secondary,
                lineHeight: "1.6",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <span
                style={{ fontWeight: 600, color: theme.palette.text.primary }}
              >
                Note:
              </span>
              <span>
                Choose Warehouse → Rack. After selecting a Rack, enter the Box
                ID or click Generate to create a system-generated Box ID.
              </span>
            </CustomTypography>
          </Box>

          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Warehouse{" "}
                <span
                  style={{
                    color: theme.palette.error.main,
                    marginLeft: 4,
                  }}
                >
                  *
                </span>
              </CustomTypography>
              <AppSearchDropDown2
                isMultiSelect={false}
                title={"Select Warehouse"}
                local={true}
                backgroundColor={theme.palette.customColors.grayBackGroundColor}
                borderColor={theme.palette.customColors.greyBodyBackground}
                minWidth="13.938rem"
                list={
                  warehouseStore.warehouses?.map((item) => {
                    return { id: String(item?.id), value: String(item?.id) };
                  }) || []
                }
                value={
                  warehouseStore.warehouses?.length > 0 &&
                  (swageazyInventoryStore.inwardRequestFormData?.warehouse_id ||
                    swageazyInventoryStore.inwardRequestFormData
                      ?.warehouse_name)
                    ? (() => {
                        const warehouseId =
                          swageazyInventoryStore.inwardRequestFormData
                            .warehouse_id;
                        const warehouseName =
                          swageazyInventoryStore.inwardRequestFormData
                            .warehouse_name;

                        let warehouse: Warehouse | undefined;

                        if (warehouseId !== undefined && warehouseId !== null) {
                          warehouse = warehouseStore.warehouses?.find(
                            (w) => String(w?.id) === String(warehouseId),
                          );
                        }

                        if (!warehouse && warehouseName) {
                          warehouse = warehouseStore.warehouses?.find(
                            (w) =>
                              String(w?.id) === warehouseName ||
                              w?.city_sku === warehouseName,
                          );
                        }

                        return warehouse
                          ? [
                              {
                                id: String(warehouse?.id),
                                value: String(warehouse?.id),
                              },
                            ]
                          : [];
                      })()
                    : []
                }
                handleSelect={(selectedItem: FilterValue[]) => {
                  if (selectedItem && selectedItem.length > 0) {
                    const warehouseUpdate: SwageazyInventoryInwardRequestFormData =
                      {};

                    warehouseUpdate.warehouse_id = selectedItem[0].id;
                    warehouseUpdate.warehouse_name = selectedItem[0].value;

                    swageazyInventoryStore.setInwardRequestFormData(
                      warehouseUpdate,
                    );

                    handleWarehouseRackSearch(selectedItem[0].id);
                  } else {
                    swageazyInventoryStore.setInwardRequestFormData({
                      warehouse_id: undefined,
                      warehouse_name: undefined,
                    });

                    handleWarehouseRackSearch(undefined);
                  }
                }}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Rack{" "}
                <span
                  style={{
                    color: theme.palette.error.main,
                    marginLeft: 4,
                  }}
                >
                  *
                </span>
              </CustomTypography>
              <AppSearchDropDown2
                title="Select Rack"
                list={
                  warehouseStore.warehouseRacks?.map((item) => {
                    return {
                      id: String(item?.id),
                      value: String(item?.id),
                    };
                  }) || []
                }
                value={
                  swageazyInventoryStore.inwardRequestFormData?.current_rack
                    ? [
                        {
                          id: swageazyInventoryStore.inwardRequestFormData
                            ?.current_rack,
                          value:
                            swageazyInventoryStore.inwardRequestFormData
                              ?.current_rack,
                        },
                      ]
                    : []
                }
                handleSelect={async (selectedItem: FilterValue[]) => {
                  if (selectedItem?.length > 0) {
                    const selectedRackId = selectedItem[0]?.id || "";
                    swageazyInventoryStore.setInwardRequestFormData({
                      current_rack: selectedRackId,
                      box_id: undefined,
                    });
                    swageazyInventoryStore.setInwardRequestFormData({
                      current_rack: selectedRackId,
                      box_id: undefined,
                    });
                  } else {
                    swageazyInventoryStore.setInwardRequestFormData({
                      current_rack: undefined,
                      box_id: undefined,
                    });
                  }
                }}
                local={true}
                minWidth="100%"
                borderRadius="8px"
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  justifyContent: "space-between",
                  marginBottom: 1,
                }}
              >
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600 }}
                >
                  Box ID{" "}
                  <span
                    style={{
                      color: theme.palette.error.main,
                      marginLeft: 4,
                    }}
                  >
                    *
                  </span>
                </CustomTypography>

                <IconButton
                  disabled={
                    !!swageazyInventoryStore.inwardRequestFormData?.box_id
                  }
                  sx={{
                    color: swageazyInventoryStore.inwardRequestFormData?.box_id
                      ? theme.palette.customColors.greyTextColor
                      : theme.palette.customColors.pinkDark,
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                    "&.Mui-disabled": {
                      color: theme.palette.customColors.greyTextColor,
                    },
                  }}
                  size="small"
                  onClick={() => {
                    if (
                      !swageazyInventoryStore.inwardRequestFormData
                        ?.warehouse_id
                    ) {
                      showError(
                        "Validation Error",
                        "Please select a Warehouse first before generating Box ID.",
                      );
                      return;
                    }
                    if (
                      !swageazyInventoryStore.inwardRequestFormData
                        ?.current_rack
                    ) {
                      showError(
                        "Validation Error",
                        "Please select a Rack ID first before generating Box ID.",
                      );
                      return;
                    }
                    swageazyInventoryStore
                      .generateBoxId(
                        swageazyInventoryStore.inwardRequestFormData!
                          .current_rack!,
                        swageazyInventoryStore.inwardRequestFormData!
                          .warehouse_id!,
                      )
                      .catch((error) => {
                        showError(
                          "Error",
                          "Failed to generate box ID. Please try again.",
                        );
                      });
                  }}
                >
                  <CustomTypography
                    variant="heading7"
                    color={
                      swageazyInventoryStore.inwardRequestFormData?.box_id
                        ? theme.palette.customColors.greyTextColor
                        : theme.palette.customColors.pinkDark
                    }
                  >
                    {swageazyInventoryStore.inwardRequestFormData?.box_id
                      ? "Generated"
                      : "Generate"}
                  </CustomTypography>
                </IconButton>
              </Box>
              <CustomTextField
                width="100%"
                placeholder="Enter Box ID or click Generate"
                value={
                  swageazyInventoryStore.inwardRequestFormData?.box_id || ""
                }
                onChange={(e) => {
                  swageazyInventoryStore.setInwardRequestFormData({
                    box_id: e.target.value,
                  });
                }}
              />
            </Grid2>
          </Grid2>

          <Box sx={{ mb: 3 }}>
            <CustomTypography
              variant="heading6"
              style={{ fontWeight: 600, marginBottom: 16 }}
            >
              Images (Optional)
            </CustomTypography>
            <Box sx={{ width: "100%", maxWidth: "100%" }}>
              <MediaAssetsUpload
                key={`inward-images-${
                  swageazyInventoryStore.inwardRequestFormData?.po_id || "new"
                }`}
                existingImageList={[]}
                newImageList={
                  (swageazyInventoryStore.inwardRequestFormData?.images ||
                    []) as File[]
                }
                multiple={true}
                uploadText="Upload Image"
                fileInfo="PNG, JPG up to 10MB each"
                removeImage={(image) => {
                  const fileIndex =
                    typeof image.id === "string" && image.id.startsWith("file-")
                      ? parseInt(image.id.replace("file-", ""), 10)
                      : Number(image.id);

                  if (fileIndex !== undefined && fileIndex >= 0) {
                    const updatedFiles =
                      swageazyInventoryStore.inwardRequestFormData?.images?.filter(
                        (_, index) => index !== fileIndex,
                      ) || [];
                    swageazyInventoryStore.setInwardRequestFormData({
                      images: updatedFiles,
                    });
                  }
                }}
                addImagesToBeUploaded={(files) => {
                  swageazyInventoryStore.setInwardRequestFormData({
                    images: [
                      ...(swageazyInventoryStore.inwardRequestFormData
                        ?.images || []),
                      ...files,
                    ],
                  });
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "10px 16px 10px", sm: "16px 20px 20px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <AppButton
            onClick={onClose}
            variant="grayscale"
            label="Cancel"
            customStyles={{
              borderColor: theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.greyTextColor,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-1px)",
              },
            }}
          />

          <AppButton
            onClick={handleSubmit}
            variant="primary"
            label="Submit"
            disabled={!canSubmit()}
            customStyles={{
              backgroundColor: canSubmit()
                ? theme.palette.customColors.EmeraldGreen
                : theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.white,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: canSubmit()
                  ? theme.palette.customColors.greenDark
                  : theme.palette.customColors.greyDarkborder,
                transform: canSubmit() ? "translateY(-1px)" : "none",
                boxShadow: canSubmit()
                  ? `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`
                  : "none",
              },
              "&:disabled": {
                backgroundColor: theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.white,
                cursor: "not-allowed",
              },
            }}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
};
export default observer(SwageazyInventoryInwardPopup);
